import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

/**
 * YouTubeShareContext - Share YouTube videos between chat and voice channel
 * Uses Supabase Realtime broadcast for cross-user sync.
 * When someone pastes a YouTube link in chat and clicks "Play Together",
 * it broadcasts to everyone in the same channel.
 */

const YouTubeShareContext = createContext(null)

export function YouTubeShareProvider({ children }) {
  const [pendingVideo, setPendingVideo] = useState(null) // { videoId, title, url, sharedBy }
  const [activeVideo, setActiveVideo] = useState(null) // Currently playing
  const [playbackState, setPlaybackState] = useState('paused') // 'playing' | 'paused'
  const [currentTime, setCurrentTime] = useState(0)
  const [currentChannelId, setCurrentChannelId] = useState(null)
  const [isHost, setIsHost] = useState(false)

  const channelRef = useRef(null)

  // Subscribe to a channel for YouTube sync
  const joinChannel = useCallback((channelId) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
    }

    if (!channelId) {
      setCurrentChannelId(null)
      return
    }

    setCurrentChannelId(channelId)

    const channel = supabase.channel(`youtube-sync:${channelId}`)

    channel
      .on('broadcast', { event: 'video-share' }, ({ payload }) => {
        console.log('[YouTubeShare] Received video share:', payload)
        setActiveVideo({
          videoId: payload.videoId,
          url: payload.url,
          title: payload.title,
          sharedBy: payload.sharedBy,
          timestamp: payload.timestamp
        })
        setPlaybackState('paused')
        setCurrentTime(0)
        setIsHost(false) // We received it, so we're not the host
      })
      .on('broadcast', { event: 'playback-sync' }, ({ payload }) => {
        // Only update if we're not the host (avoid feedback loop)
        if (!isHost) {
          setPlaybackState(payload.state)
          setCurrentTime(payload.time)
        }
      })
      .on('broadcast', { event: 'video-stop' }, () => {
        console.log('[YouTubeShare] Video stopped by host')
        setActiveVideo(null)
        setPlaybackState('paused')
        setCurrentTime(0)
        setIsHost(false)
      })
      .subscribe()

    channelRef.current = channel
  }, [isHost])

  // Leave channel on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
      }
    }
  }, [])

  // Called from ChatArea when user wants to share a video
  const shareVideo = useCallback((videoId, url, title, sharedBy) => {
    const videoData = { videoId, url, title, sharedBy, timestamp: Date.now() }

    // Set local state
    setActiveVideo(videoData)
    setPlaybackState('paused')
    setCurrentTime(0)
    setIsHost(true) // We shared it, so we're the host

    // Broadcast to channel
    if (channelRef.current) {
      channelRef.current.send({
        type: 'broadcast',
        event: 'video-share',
        payload: videoData
      })
    }
  }, [])

  // Stop playing
  const stopVideo = useCallback(() => {
    setActiveVideo(null)
    setPlaybackState('paused')
    setCurrentTime(0)
    setIsHost(false)

    // Broadcast stop to channel
    if (channelRef.current) {
      channelRef.current.send({
        type: 'broadcast',
        event: 'video-stop',
        payload: {}
      })
    }
  }, [])

  // Update playback state (for sync) - called by host
  const updatePlayback = useCallback((state, time) => {
    setPlaybackState(state)
    setCurrentTime(time)

    // Broadcast sync to channel (only if we're host)
    if (isHost && channelRef.current) {
      channelRef.current.send({
        type: 'broadcast',
        event: 'playback-sync',
        payload: { state, time }
      })
    }
  }, [isHost])

  return (
    <YouTubeShareContext.Provider
      value={{
        pendingVideo,
        setPendingVideo,
        activeVideo,
        playbackState,
        currentTime,
        isHost,
        currentChannelId,
        joinChannel,
        shareVideo,
        stopVideo,
        updatePlayback,
      }}
    >
      {children}
    </YouTubeShareContext.Provider>
  )
}

export function useYouTubeShareContext() {
  const context = useContext(YouTubeShareContext)
  // Return null instead of throwing - allows usage outside provider (graceful degradation)
  return context
}

export default YouTubeShareContext
